import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { ILoginResponse } from '../models/ILoginResponse';
import { IAPIResponse } from '../../shared/models/IApiResponse';
import { environment } from 'src/environments/environment';
import { IGlimpseUser } from '../models/IGlimpseUser';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SwPush } from '@angular/service-worker';
import dayjs from 'dayjs';
import { PeriodPreference, ReportPreference } from '../../admin/models/ReportPreference';
import { IRefreshTokenResponse } from '../models/IRefreshTokenResponse';
import { updateUser } from '../../../store/auth';
import { Store } from '@ngrx/store';
import * as fromAuth from '../../../store/auth';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  static getJwt() {
    let jwt = localStorage.getItem('glimpse_token');
    return jwt ?? localStorage.getItem('glimpse_anonymous_token');
  }

  static getScheme(): string {
    let jwt = localStorage.getItem('glimpse_token');
    return jwt ? 'Bearer ' : 'ReportToken ';
  }

  static setJwt(token: string) {
    localStorage.setItem('glimpse_token', token);
  }

  static setReportToken(token: string) {
    if (!token) localStorage.removeItem('glimpse_anonymous_token');
    localStorage.setItem('glimpse_anonymous_token', token);
  }

  public loginWithUsernamePassword(username: string, password: string): Observable<ILoginResponse> {
    // Get IANA time zone from the browser
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return this.http
      .post<
        IAPIResponse<ILoginResponse>
      >(`${environment.apiBase}/v1.0/auth/login`, { username, password, timeZone }, { withCredentials: true })
      .pipe(
        map(res => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res.data;
        }),
        catchError((err: Error) => {
          if (err instanceof HttpErrorResponse) {
            throw new Error('Error communicating with Glimpse. Please try again.');
          }
          throw err;
        }),
      );
  }

  requestPasswordReset(email: string): Observable<any> {
    return this.http.post<IAPIResponse<any>>(environment.apiBase + '/v1.0/auth/forgotpassword', { email }).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error);
        }
        return response.data;
      }),
    );
  }

  resetPassword(email: string, password: string, token: string): Observable<boolean> {
    return this.http
      .post<IAPIResponse<boolean>>(environment.apiBase + '/v1.0/auth/resetpassword', {
        email,
        password,
        code: token,
      })
      .pipe(
        map(response => {
          if (response.error) {
            throw new Error(response.error);
          }
          return response.data;
        }),
      );
  }

  changePassword(oldPassword: string, newPassword: string): Observable<boolean> {
    return this.http
      .post<IAPIResponse<boolean>>(
        environment.apiBase + '/v1.0/auth/changepassword',
        {
          oldPassword,
          newPassword,
        },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          if (response.error) {
            throw new Error(response.error);
          }
          return response.data;
        }),
      );
  }

  dismissPush() {
    localStorage.setItem('glimpse_push_dismissed', dayjs().toString());
  }

  pushDismissed() {
    let dismissedAt = localStorage.getItem('glimpse_push_dismissed');
    if (!dismissedAt) return of(false);

    return of(dayjs(dismissedAt).add(30, 'day') > dayjs());
  }

  existingPushSubscription() {
    return this.push.subscription;
  }

  requestForPush() {
    return this.push.requestSubscription({ serverPublicKey: environment.pushVapidPublicKey });
  }

  addPushSubscription(subscription: PushSubscription) {
    return this.http.post(`${environment.apiBase}/v1.0/share/enable-push`, subscription);
  }

  disablePush(sub?: PushSubscription) {
    return sub
      ? this.http.put(`${environment.apiBase}/v1.0/share/remove-push`, sub)
      : this.http.delete(`${environment.apiBase}/v1.0/share/remove-push`);
  }

  loadProfile() {
    return this.http.get<IAPIResponse<IGlimpseUser>>(`${environment.apiBase}/v1.0/auth/me`);
  }

  updateProfile(profile: { email: string; lastName: string; firstName: string }): Observable<IGlimpseUser> {
    return this.http.post<IAPIResponse<IGlimpseUser>>(environment.apiBase + '/v1.0/auth/me', profile).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error);
        }
        return response.data;
      }),
    );
  }

  updatePreference(prefs: {
    sms: boolean;
    email: boolean;
    reports: ReportPreference;
    periods: PeriodPreference[];
    lostItems: boolean;
    time: string;
  }): Observable<IGlimpseUser> {
    return this.http.post<IAPIResponse<IGlimpseUser>>(environment.apiBase + '/v1.0/auth/preference', prefs).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error);
        }
        return response.data;
      }),
    );
  }

  updateEscalationPreference(enabled: boolean): Observable<IGlimpseUser> {
    return this.http.post<IAPIResponse<IGlimpseUser>>(environment.apiBase + '/v1.0/auth/escalations', { enabled }).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error);
        }
        return response.data;
      }),
    );
  }

  updatePhoneNumber(phoneNumber: string): Observable<void> {
    return this.http.put<void>(`${environment.apiBase}/v1.0/auth/phonenumber`, { phoneNumber });
  }

  constructor(
    private http: HttpClient,
    private jwt: JwtHelperService,
    private push: SwPush,
    private store: Store<{ auth: fromAuth.AuthState }>,
  ) {}

  refreshToken() {
    let accessToken = localStorage.getItem('glimpse_token');
    if (!accessToken || !this.jwt.isTokenExpired(accessToken)) return of(false);
    return this.http
      .put<IAPIResponse<IRefreshTokenResponse>>(
        `${environment.apiBase}/v1.0/auth/me`,
        {
          oldAccessToken: accessToken,
        },
        { withCredentials: true },
      )
      .pipe(
        map(response => {
          AuthService.setJwt(response.data.accessToken);
          this.store.dispatch(updateUser({ user: response.data.user }));
          return true;
        }),
      );
  }
}
